import { useState, useEffect } from "react";
import { Camera, Edit3, Info } from "lucide-react";
import { BodySVG } from "@/components/we360/SVGIllustrations";
import { TextInput } from "@/components/we360/TextInput";
import { CalculatedBadge } from "@/components/we360/CalculatedBadge";
import { AlertBanner } from "@/components/we360/AlertBanner";
import VitalsMeasurement from "@/components/we360/VitalsMeasurement";
import { VitalsErrorBoundary } from "@/components/we360/VitalsErrorBoundary";
import { isAuthorizedDomain, isSDKSupported } from "@/services/fhvitals";
import { useAuth } from "@/contexts/AuthContext";
import { saveMeasurement, syncMetricsToGCP } from "@/services/measurements";

export default function Step3Clinical({ data, setField, errors, onBlur, imc, imcClass, bpClass, gluClass }) {
  const { user } = useAuth();
  const [showVitalsModal, setShowVitalsModal] = useState(false);
  const [showManualInputs, setShowManualInputs] = useState(true);
  const [faceheartResults, setFaceheartResults] = useState(null); // Guardar resultados de FaceHeart
  
  const oxiVal = parseFloat(data.oximetria);
  const showOxiAlert = !isNaN(oxiVal) && oxiVal < 95;

  // Verificar si el SDK está disponible
  const isDomainAuthorized = isAuthorizedDomain();
  const sdkCheck = isSDKSupported();
  const canUseCameraSDK = isDomainAuthorized && sdkCheck.supported;

  // Log de verificación al cargar el componente
  useEffect(() => {
    console.log('[Step3Clinical] 🔍 Verificación SDK:', {
      dominio: window.location.hostname,
      isDomainAuthorized,
      sdkSupported: sdkCheck.supported,
      sdkReason: sdkCheck.reason || 'OK',
      canUseCameraSDK
    });
  }, []);

  // Calcular edad del usuario (necesaria para el SDK)
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleVitalsComplete = async (results) => {
    console.log('[Step3Clinical] 🎉 Medición completada, resultados recibidos:', results);
    
    // Guardar resultados completos para mostrar resumen
    setFaceheartResults(results);
    
    // Auto-llenar campos con resultados del SDK
    if (results.bloodPressureSystolic) {
      console.log('[Step3Clinical] 📝 Auto-llenando PA sistólica:', results.bloodPressureSystolic);
      setField("paSistolica", results.bloodPressureSystolic.toString());
    }
    if (results.bloodPressureDiastolic) {
      console.log('[Step3Clinical] 📝 Auto-llenando PA diastólica:', results.bloodPressureDiastolic);
      setField("paDiastolica", results.bloodPressureDiastolic.toString());
    }
    if (results.oxygenSaturation) {
      console.log('[Step3Clinical] 📝 Auto-llenando oximetría:', results.oxygenSaturation);
      setField("oximetria", results.oxygenSaturation.toString());
    }
    
    // Guardar TODAS las métricas en el profile
    if (results.heartRate) {
      console.log('[Step3Clinical] 💓 Guardando HR:', results.heartRate);
      setField("heartRate", results.heartRate);
    }
    if (results.heartRateVariability) {
      console.log('[Step3Clinical] 📊 Guardando HRV:', results.heartRateVariability);
      setField("heartRateVariability", results.heartRateVariability);
    }
    if (results.respirationRate) {
      console.log('[Step3Clinical] 🌬️ Guardando RR:', results.respirationRate);
      setField("respirationRate", results.respirationRate);
    }
    if (results.stressIndex !== null) {
      console.log('[Step3Clinical] 😰 Guardando índice de estrés:', results.stressIndex);
      setField("stressIndex", results.stressIndex);
    }
    if (results.cardiovascularAge) {
      console.log('[Step3Clinical] 🫀 Guardando edad cardiovascular:', results.cardiovascularAge);
      setField("cardiovascularAge", results.cardiovascularAge);
    }
    if (results.cardiovascularBMI) {
      console.log('[Step3Clinical] 📈 Guardando BMI cardiovascular:', results.cardiovascularBMI);
      setField("cardiovascularBMI", results.cardiovascularBMI);
    }
    if (results.cvdRisk !== null) {
      console.log('[Step3Clinical] ⚠️ Guardando riesgo cardiovascular:', results.cvdRisk);
      setField("cvdRisk", results.cvdRisk);
    }
    if (results.wellnessScore) {
      console.log('[Step3Clinical] ✨ Guardando wellness score:', results.wellnessScore);
      setField("wellnessScore", results.wellnessScore);
    }
    if (results.ansIndexPNS !== null && results.ansIndexPNS !== undefined) {
      setField("ansIndexPNS", results.ansIndexPNS);
    }
    if (results.ansIndexSNS !== null && results.ansIndexSNS !== undefined) {
      setField("ansIndexSNS", results.ansIndexSNS);
    }
    if (results.hba1cValue != null) setField("hba1c", results.hba1cValue);
    if (results.hemoglobin != null) setField("hemoglobin", results.hemoglobin);
    if (results.cholesterolValue != null) setField("cholesterol", results.cholesterolValue);
    if (results.shockIndex != null) setField("shockIndex", results.shockIndex);
    if (results.prq != null) setField("prq", results.prq);
    if (results.hrvRMSSD != null) setField("hrvRMSSD", results.hrvRMSSD);
    if (results.hrvSDNN != null) setField("hrvSDNN", results.hrvSDNN);
    
    setField("vitalsMeasurementMethod", "camera");
    setField("vitalsMeasurementDate", results.measurementDate);

    // Persistir medición inmutable en Outbox users/{uid}/measurements y sincronizar con FHIR Store
    if (user?.uid && !user.isDemo) {
      const fieldsToSave = {
        paSistolica: results.bloodPressureSystolic || null,
        paDiastolica: results.bloodPressureDiastolic || null,
        oximetria: results.oxygenSaturation || null,
        heartRate: results.heartRate || null,
        heartRateVariability: results.heartRateVariability || null,
        respirationRate: results.respirationRate || null,
        stressIndex: results.stressIndex != null ? Math.round(results.stressIndex) : null,
        cardiovascularAge: results.cardiovascularAge != null ? Math.round(results.cardiovascularAge) : null,
        cardiovascularBMI: results.cardiovascularBMI || null,
        cvdRisk: results.cvdRisk ?? null,
        wellnessScore: results.wellnessScore || null,
        ansIndexPNS: results.ansIndexPNS ?? null,
        ansIndexSNS: results.ansIndexSNS ?? null,
        hba1c: results.hba1cValue ?? null,
        hemoglobin: results.hemoglobin ?? null,
        cholesterol: results.cholesterolValue ?? null,
        shockIndex: results.shockIndex ?? null,
        prq: results.prq ?? null,
        hrvRMSSD: results.hrvRMSSD ?? null,
        hrvSDNN: results.hrvSDNN ?? null,
        ...(data.peso ? { peso: parseFloat(data.peso) } : {}),
        ...(data.talla ? { talla: parseFloat(data.talla) } : {}),
      };
      try {
        const savedMeas = await saveMeasurement(user.uid, {
          method: "camera",
          fields: fieldsToSave,
          note: "Escaneo facial rPPG en Formulario F1",
        });
        syncMetricsToGCP(user.email || data.email, fieldsToSave, {
          uid: user.uid,
          measurementId: savedMeas?.id || null,
        }).catch(() => {});
      } catch (err) {
        console.warn("[Step3Clinical] Error guardando medición en Outbox:", err);
      }
    }
    
    console.log('[Step3Clinical] ✅ Todos los campos actualizados');
    
    setShowVitalsModal(false);
    setShowManualInputs(true);
  };

  const openCameraMeasurement = () => {
    console.log('[Step3Clinical] 🎥 Intentando abrir medición con cámara...');
    console.log('[Step3Clinical] 📊 Datos actuales:', { peso: data.peso, talla: data.talla, fechaNacimiento: data.fechaNacimiento, sexo: data.sexo });
    
    // Validar que tenemos los datos necesarios para el SDK
    if (!data.peso || !data.talla) {
      console.warn('[Step3Clinical] ⚠️ Faltan peso o talla');
      alert("Por favor ingresa tu peso y talla primero");
      return;
    }
    if (!data.fechaNacimiento) {
      console.warn('[Step3Clinical] ⚠️ Falta fecha de nacimiento');
      alert("Por favor completa los datos personales (paso anterior)");
      return;
    }
    
    console.log('[Step3Clinical] ✅ Validaciones OK, abriendo modal...');
    setShowVitalsModal(true);
  };

  const userAge = calculateAge(data.fechaNacimiento);
  const userSex = data.sexo === "Masculino" ? 1 : 0;

  return (
    <div>
      <div className="text-center mb-4">
        <BodySVG imc={imc ? parseFloat(imc) : null} bpClass={bpClass?.key} />
      </div>

      {/* Sub-tarjeta 1: Composición Corporal */}
      <div className="glass-card p-5 rounded-3xl mb-5 text-left border border-white/40 shadow-sm">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#00628F] mb-4">
          Composición Corporal
        </h3>

        <div className="flex gap-2.5 mb-3">
          <div className="flex-1">
            <TextInput
              label="Peso"
              value={data.peso}
              onChange={(v) => setField("peso", v)}
              onBlur={() => onBlur("peso")}
              placeholder="72.5"
              suffix="kg"
              type="number"
              min="20"
              max="300"
              step="0.1"
              error={errors.getError("peso")}
              testId="input-peso"
            />
          </div>
          <div className="flex-1">
            <TextInput
              label="Talla"
              value={data.talla}
              onChange={(v) => setField("talla", v)}
              onBlur={() => onBlur("talla")}
              placeholder="168"
              suffix="cm"
              type="number"
              min="50"
              max="250"
              error={errors.getError("talla")}
              testId="input-talla"
            />
          </div>
        </div>

        {imc && imcClass && (
          <CalculatedBadge label="IMC calculado" value={imc} category={imcClass.label} categoryColor={imcClass.color} testId="badge-imc" />
        )}

        <div className="mt-3">
          <TextInput
            label="Cintura abdominal"
            value={data.cintura}
            onChange={(v) => setField("cintura", v)}
            onBlur={() => onBlur("cintura")}
            placeholder="85"
            suffix="cm"
            type="number"
            min="40"
            max="200"
            error={errors.getError("cintura")}
            testId="input-cintura"
          />
        </div>
      </div>

      {/* Sub-tarjeta 2: Signos Vitales */}
      <div className="glass-card p-5 rounded-3xl text-left border border-white/40 shadow-sm">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#00628F] mb-4">
          Signos Vitales
        </h3>
        
        {/* Botones de método de captura */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button
            type="button"
            onClick={openCameraMeasurement}
            disabled={!canUseCameraSDK}
            className={`relative flex flex-col items-center gap-2.5 p-4 rounded-2xl border transition-all ${
              canUseCameraSDK 
                ? 'border-[#2A9D8F]/30 bg-[#2A9D8F]/5 hover:bg-[#2A9D8F]/10 cursor-pointer shadow-sm hover:shadow-md' 
                : 'border-gray-200 bg-gray-50/50 cursor-not-allowed opacity-60'
            }`}
            title={!canUseCameraSDK ? (isDomainAuthorized ? sdkCheck.reason : 'Disponible solo en producción') : 'Medir con cámara'}
          >
            <Camera size={26} className={canUseCameraSDK ? 'text-[#2A9D8F]' : 'text-gray-400'} />
            <div className="text-center">
              <p className={`text-xs font-bold ${canUseCameraSDK ? 'text-[#2A9D8F]' : 'text-gray-500'}`}>
                Medir con cámara
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5 font-medium">~50 segundos</p>
            </div>
            {!canUseCameraSDK && (
              <span className="absolute top-1.5 right-1.5 text-[9px] px-2 py-0.5 bg-orange-500 text-white rounded-full font-bold">
                PROD
              </span>
            )}
          </button>
          
          <button
            type="button"
            onClick={() => setShowManualInputs(!showManualInputs)}
            className={`flex flex-col items-center gap-2.5 p-4 rounded-2xl border transition-all cursor-pointer ${
              showManualInputs 
                ? 'border-[#00628F]/30 bg-[#00628F]/5 shadow-sm' 
                : 'border-gray-200 bg-white hover:bg-gray-50/80 shadow-sm hover:shadow-md'
            }`}
          >
            <Edit3 size={26} className={showManualInputs ? 'text-[#00628F]' : 'text-gray-500'} />
            <div className="text-center">
              <p className={`text-xs font-bold ${showManualInputs ? 'text-[#00628F]' : 'text-gray-700'}`}>
                Ingresar manual
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5 font-medium">Valores conocidos</p>
            </div>
          </button>
        </div>
        
        {!canUseCameraSDK && (
          <div className="text-[10px] px-3 py-2 bg-[#E3F0F5] border border-[#00628F]/10 rounded-xl mb-4 font-medium" style={{ color: '#00628F' }}>
            💡 La medición por cámara (rPPG) estará disponible cuando la app esté en producción ({AUTHORIZED_DOMAINS.slice(0, 2).join(', ')})
          </div>
        )}

        {/* Inputs manuales */}
        {showManualInputs && (
          <div className="p-4 rounded-2xl border border-white/50 mb-4 shadow-inner" style={{ background: "rgba(250, 248, 240, 0.8)" }}>
            <p className="text-xs font-bold text-[#71644C] mb-3 uppercase tracking-wider">Presión arterial</p>
            <div className="flex gap-2.5 mb-3">
              <div className="flex-1">
                <TextInput
                  label="Sistólica (Máxima)"
                  value={data.paSistolica}
                  onChange={(v) => setField("paSistolica", v)}
                  onBlur={() => onBlur("paSistolica")}
                  placeholder="120"
                  suffix="mmHg"
                  type="number"
                  min="60"
                  max="250"
                  error={errors.getError("paSistolica")}
                  testId="input-pa-sistolica"
                />
              </div>
              <div className="flex-1">
                <TextInput
                  label="Diastólica (Mínima)"
                  value={data.paDiastolica}
                  onChange={(v) => setField("paDiastolica", v)}
                  onBlur={() => onBlur("paDiastolica")}
                  placeholder="80"
                  suffix="mmHg"
                  type="number"
                  min="30"
                  max="150"
                  error={errors.getError("paDiastolica")}
                  testId="input-pa-diastolica"
                />
              </div>
            </div>

            {bpClass && (
              <div className="mb-4">
                <CalculatedBadge label="Presión arterial" value={`${data.paSistolica}/${data.paDiastolica}`} unit="mmHg" category={bpClass.label} categoryColor={bpClass.color} testId="badge-presion" />
              </div>
            )}

            {bpClass?.isCrisis && (
              <div className="mb-4">
                <AlertBanner variant="danger" testId="alert-crisis-pa">
                  ALERTA: Los valores ingresados sugieren una crisis hipertensiva. Si estos son tus valores actuales, busca atención médica de urgencia.
                </AlertBanner>
              </div>
            )}

            <p className="text-xs font-bold text-[#71644C] mb-3 uppercase tracking-wider">Metabolismo y Oxígeno</p>
            <div className="flex gap-2.5">
              <div className="flex-1">
                <TextInput
                  label="Glucemia ayunas"
                  value={data.glucemia}
                  onChange={(v) => setField("glucemia", v)}
                  onBlur={() => onBlur("glucemia")}
                  placeholder="95"
                  suffix="mg/dl"
                  type="number"
                  min="30"
                  max="600"
                  error={errors.getError("glucemia")}
                  testId="input-glucemia"
                />
              </div>
              <div className="flex-1">
                <TextInput
                  label="Oximetría"
                  value={data.oximetria}
                  onChange={(v) => setField("oximetria", v)}
                  onBlur={() => onBlur("oximetria")}
                  placeholder="98"
                  suffix="%"
                  type="number"
                  min="50"
                  max="100"
                  error={errors.getError("oximetria")}
                  testId="input-oximetria"
                />
              </div>
            </div>

            {gluClass && (
              <div className="mt-3">
                <CalculatedBadge label="Glucemia" value={data.glucemia} unit="mg/dl" category={gluClass.label} categoryColor={gluClass.color} testId="badge-glucemia" />
              </div>
            )}

            {showOxiAlert && (
              <div className="mt-3">
                <AlertBanner variant="danger" testId="alert-oximetria">
                  Oximetría por debajo de 95%. Se recomienda consultar con un profesional de salud.
                </AlertBanner>
              </div>
            )}
          </div>
        )}

        {/* Resumen de medición con FaceHeart */}
        {faceheartResults && data.vitalsMeasurementMethod === 'camera' && (
          <div className="mt-4 p-4 bg-gradient-to-r from-teal-50/70 to-cyan-50/70 border border-[#2A9D8F]/20 rounded-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-full bg-[#2A9D8F] flex items-center justify-center shadow-sm">
                <span className="text-white text-base font-bold">✓</span>
              </div>
              <div>
                <h3 className="text-sm font-bold text-teal-900">Medición con cámara completada</h3>
                <p className="text-xs text-teal-700">13 métricas cardiovasculares capturadas</p>
              </div>
            </div>

            {/* Grid de métricas */}
            <div className="grid grid-cols-2 gap-2.5 mb-3">
              <MetricCard 
                label="Frecuencia cardíaca" 
                value={faceheartResults.heartRate} 
                unit="lpm"
                userAge={userAge}
              />
              <MetricCard 
                label="Presión arterial" 
                value={faceheartResults.bloodPressureSystolic && faceheartResults.bloodPressureDiastolic 
                  ? `${faceheartResults.bloodPressureSystolic}/${faceheartResults.bloodPressureDiastolic}` 
                  : null
                } 
                unit="mmHg"
                userAge={userAge}
              />
              <MetricCard 
                label="Saturación O₂" 
                value={faceheartResults.oxygenSaturation} 
                unit="%"
                userAge={userAge}
              />
              <MetricCard 
                label="Frecuencia resp." 
                value={faceheartResults.respirationRate} 
                unit="rpm"
                userAge={userAge}
              />
            </div>

            {/* Métricas cardiovasculares avanzadas */}
            {(faceheartResults.cardiovascularAge || faceheartResults.cvdRisk !== null || faceheartResults.wellnessScore) && (
              <>
                <div className="border-t border-teal-100 my-4"></div>
                <p className="text-xs font-bold text-teal-900 mb-2">Análisis cardiovascular:</p>
                <div className="grid grid-cols-2 gap-2.5">
                  {faceheartResults.cardiovascularAge && (
                    <MetricCard 
                      label="Edad cardiovascular" 
                      value={faceheartResults.cardiovascularAge} 
                      unit="años"
                      highlight={faceheartResults.cardiovascularAge > (calculateAge(data.fechaNacimiento) || 0) + 5}
                      userAge={userAge}
                    />
                  )}

                  {faceheartResults.wellnessScore && (
                    <MetricCard 
                      label="Bienestar" 
                      value={faceheartResults.wellnessScore} 
                      unit="/10"
                      userAge={userAge}
                    />
                  )}
                  {faceheartResults.stressIndex !== null && (
                    <MetricCard 
                      label="Índice de estrés" 
                      value={faceheartResults.stressIndex} 
                      unit="/1000"
                      userAge={userAge}
                    />
                  )}
                </div>
              </>
            )}

            {/* Nota informativa */}
            <div className="mt-4 text-[10px] text-teal-800 bg-white/60 p-2.5 rounded-xl border border-teal-100/50 font-medium">
              📊 Todas las métricas se guardarán automáticamente en tu perfil y estarán disponibles en el Dashboard
            </div>
          </div>
        )}
      </div>

      {/* Modal de medición con cámara */}
      {showVitalsModal && (
        <VitalsErrorBoundary onError={() => setShowVitalsModal(false)}>
          <VitalsMeasurement
            isOpen={showVitalsModal}
            onClose={() => setShowVitalsModal(false)}
            onComplete={handleVitalsComplete}
            userInfo={{
              age: userAge,
              sex: userSex,
              height: parseFloat(data.talla),
              weight: parseFloat(data.peso),
              bp_group: bpClass?.key === 'hypertension' ? 'hypertension' : bpClass?.key === 'prehypertension' ? 'prehypertension' : 'normal'
            }}
          />
        </VitalsErrorBoundary>
      )}
    </div>
  );
}

const AUTHORIZED_DOMAINS = [
  'we360-stg.saluddigital360.com',
  'we360.saluddigital360.com'
];

const METRIC_EXPLANATIONS = {
  "Frecuencia cardíaca": "Frecuencia con la que late el corazón por minuto (lpm). El rango normal en reposo en adultos es de 60 a 100 lpm.",
  "Presión arterial": "Fuerza que ejerce la sangre contra las paredes arteriales. Se compone de la sistólica (máxima) y la diastólica (mínima). Rango normal: < 120/80 mmHg.",
  "PA Sistólica": "Presión máxima en las arterias durante la contracción del corazón (sístole). Nivel normal: menor a 120 mmHg.",
  "PA Diastólica": "Presión mínima en las arterias cuando el corazón se relaja entre latidos (diástole). Nivel normal: menor a 80 mmHg.",
  "Saturación O₂": "Porcentaje de oxígeno en los glóbulos rojos (%). Valores normales en reposo: 95% o superior.",
  "Frecuencia resp.": "Número de respiraciones por minuto (rpm). El ritmo normal en reposo para adultos es de 12 a 20 rpm.",
  "Edad cardiovascular": "Edad biológica estimada de tu sistema cardiovascular según la elasticidad de los vasos sanguíneos y onda de pulso.",
  "Riesgo CVD": "Porcentaje de riesgo de presentar un evento cardiovascular adverso (como infarto o ACV) en los próximos 10 años.",
  "Riesgo cardiovascular": "Porcentaje de riesgo de presentar un evento cardiovascular adverso en los próximos 10 años.",
  "Bienestar": "Puntuación de bienestar y balance del sistema nervioso autónomo (escala 0-10).",
  "Índice de estrés": "Escala de Baevsky. Mide el estrés regulador del sistema nervioso autónomo. <150 es normal, 150-300 moderado, 300-600 alto, y >600 es crítico."
};

// Componente para mostrar métricas individuales con indicador estilo PageSpeed
function MetricCard({ label, value, unit, highlight, userAge }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const explanation = METRIC_EXPLANATIONS[label];
  const gauge = getGaugeConfig(label, value, userAge);

  return (
    <div className={`p-3 rounded-xl border transition-all relative ${highlight ? 'bg-orange-50 border-orange-200 shadow-sm' : 'bg-white border-teal-50 shadow-sm'}`}>
      <div className="flex items-center justify-between gap-1 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-[10px] font-bold truncate" style={{ color: highlight ? '#9A3412' : '#0F766E' }}>
            {label}
          </p>
        </div>
        {explanation && (
          <div className="relative shrink-0 flex items-center justify-center">
            <button
              type="button"
              className="text-slate-400 hover:text-teal-600 p-0.5 rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-teal-500/20"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowTooltip(!showTooltip);
              }}
            >
              <Info size={11} />
            </button>
            {showTooltip && (
              <div 
                className="absolute bottom-full right-0 mb-2 w-48 p-2.5 rounded-xl text-[10px] leading-normal text-white bg-slate-900/95 backdrop-blur-md shadow-lg border border-white/10 z-30 pointer-events-none transition-all duration-200"
              >
                {explanation}
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className="text-lg font-extrabold" style={{ color: highlight ? '#C2410C' : '#0F766E' }}>
          {value !== null && value !== undefined ? value : '--'}
        </span>
        {unit && (
          <span className="text-[9px] font-bold" style={{ color: highlight ? '#9A3412' : '#14B8A6' }}>
            {unit}
          </span>
        )}
      </div>

      {/* PageSpeed-style Status Gauge Bar */}
      {value !== null && value !== undefined && gauge && (
        <div className="mt-2 w-full">
          {/* Multi-segment bar */}
          <div className="h-1.5 w-full flex relative bg-slate-100/50 rounded-full">
            {gauge.segments.map((seg, idx) => (
              <div 
                key={idx} 
                style={{ width: seg.width, backgroundColor: seg.color }}
                className={`h-full ${idx === 0 ? 'rounded-l-full' : ''} ${idx === gauge.segments.length - 1 ? 'rounded-r-full' : ''}`}
                title={`${seg.label}: ${seg.range}`}
              />
            ))}
            {/* Indicator Bullet */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border border-white bg-slate-800 shadow-md transition-all duration-500 ease-out z-10"
              style={{ left: `calc(${gauge.markerPosition}% - 5px)` }}
            />
          </div>
          {/* Status Label */}
          <div className="flex justify-between items-center mt-1">
            <span className="text-[8px] font-extrabold uppercase tracking-wider" style={{ color: gauge.statusColor }}>
              {gauge.statusLabel}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper para calcular rangos de semáforo de salud clínica tipo PageSpeed Insights
function getGaugeConfig(label, value, userAge = 35) {
  if (value === null || value === undefined) return null;
  
  // Normalizar valor numérico
  let numVal = parseFloat(value);
  if (isNaN(numVal)) {
    // Si es un rango de presión "SYS/DIA", parsear SYS para el indicador principal
    if (typeof value === 'string' && value.includes('/')) {
      const parts = value.split('/');
      numVal = parseFloat(parts[0]);
    } else {
      return null;
    }
  }

  const COLOR_GREEN = "#10B981";  // Bueno (esmeralda)
  const COLOR_YELLOW = "#F59E0B"; // Regular (ámbar)
  const COLOR_RED = "#EF4444";    // Alerta (rojo)

  let statusLabel = "Bueno";
  let statusColor = COLOR_GREEN;
  let markerPosition = 50; // Porcentaje de 0 a 100
  let segments = [];

  // Mapeador lineal auxiliar para calcular posición del marcador
  const helperMap = (val, inMin, inMax, outMin, outMax) => {
    if (inMax === inMin) return outMin;
    const pct = (val - inMin) / (inMax - inMin);
    return Math.max(outMin, Math.min(outMax, outMin + pct * (outMax - outMin)));
  };

  if (label === "Frecuencia cardíaca") {
    // Bueno: 60-100, Regular: 50-59 o 101-110, Alerta: <50 o >110
    segments = [
      { color: COLOR_YELLOW, width: "20%", label: "Bajo", range: "< 60 lpm" },
      { color: COLOR_GREEN, width: "60%", label: "Normal", range: "60 - 100 lpm" },
      { color: COLOR_RED, width: "20%", label: "Alto", range: "> 100 lpm" }
    ];

    if (numVal < 60) {
      statusLabel = numVal < 50 ? "Alerta (Bajo)" : "Regular (Bajo)";
      statusColor = numVal < 50 ? COLOR_RED : COLOR_YELLOW;
      markerPosition = helperMap(numVal, 40, 60, 5, 20);
    } else if (numVal <= 100) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 60, 100, 20, 80);
    } else {
      statusLabel = numVal > 110 ? "Alerta (Alto)" : "Regular (Alto)";
      statusColor = numVal > 110 ? COLOR_RED : COLOR_YELLOW;
      markerPosition = helperMap(numVal, 100, 140, 80, 95);
    }
  }
  else if (label === "Presión arterial" || label === "PA Sistólica") {
    // Bueno: <120, Regular: 120-139, Alerta: >=140
    segments = [
      { color: COLOR_GREEN, width: "45%", label: "Bueno", range: "< 120 mmHg" },
      { color: COLOR_YELLOW, width: "35%", label: "Regular", range: "120 - 139 mmHg" },
      { color: COLOR_RED, width: "20%", label: "Alerta", range: ">= 140 mmHg" }
    ];

    if (numVal < 120) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 90, 120, 5, 45);
    } else if (numVal < 140) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 120, 140, 45, 80);
    } else {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 140, 180, 80, 95);
    }
  }
  else if (label === "PA Diastólica") {
    // Bueno: <80, Regular: 80-89, Alerta: >=90
    segments = [
      { color: COLOR_GREEN, width: "45%", label: "Bueno", range: "< 80 mmHg" },
      { color: COLOR_YELLOW, width: "35%", label: "Regular", range: "80 - 89 mmHg" },
      { color: COLOR_RED, width: "20%", label: "Alerta", range: ">= 90 mmHg" }
    ];

    if (numVal < 80) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 50, 80, 5, 45);
    } else if (numVal < 90) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 80, 90, 45, 80);
    } else {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 90, 110, 80, 95);
    }
  }
  else if (label === "Saturación O₂") {
    // Bueno: >=95%, Regular: 92-94%, Alerta: <92%
    segments = [
      { color: COLOR_RED, width: "30%", label: "Alerta", range: "< 92%" },
      { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "92% - 94%" },
      { color: COLOR_GREEN, width: "40%", label: "Bueno", range: ">= 95%" }
    ];

    if (numVal < 92) {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 85, 92, 5, 30);
    } else if (numVal < 95) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 92, 95, 30, 60);
    } else {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 95, 100, 60, 95);
    }
  }
  else if (label === "Frecuencia resp.") {
    // Bueno: 12-20, Regular: 10-11 o 21-24, Alerta: <10 o >24
    segments = [
      { color: COLOR_YELLOW, width: "20%", label: "Bajo", range: "< 12 rpm" },
      { color: COLOR_GREEN, width: "60%", label: "Normal", range: "12 - 20 rpm" },
      { color: COLOR_RED, width: "20%", label: "Alto", range: "> 20 rpm" }
    ];

    if (numVal < 12) {
      statusLabel = numVal < 10 ? "Alerta (Bajo)" : "Regular (Bajo)";
      statusColor = numVal < 10 ? COLOR_RED : COLOR_YELLOW;
      markerPosition = helperMap(numVal, 8, 12, 5, 20);
    } else if (numVal <= 20) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 12, 20, 20, 80);
    } else {
      statusLabel = numVal > 24 ? "Alerta (Alto)" : "Regular (Alto)";
      statusColor = numVal > 24 ? COLOR_RED : COLOR_YELLOW;
      markerPosition = helperMap(numVal, 20, 28, 80, 95);
    }
  }
  else if (label === "Variabilidad (HRV)") {
    // Bueno: >50 ms, Regular: 30-50 ms, Alerta: <30 ms
    segments = [
      { color: COLOR_RED, width: "30%", label: "Alerta", range: "< 30 ms" },
      { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "30 - 50 ms" },
      { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "> 50 ms" }
    ];

    if (numVal < 30) {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 10, 30, 5, 30);
    } else if (numVal <= 50) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 30, 50, 30, 60);
    } else {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 50, 110, 60, 95);
    }
  }
  else if (label === "Índice de estrés") {
    // Bueno: <150, Regular: 150-300, Alerta: >=300
    segments = [
      { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "< 150" },
      { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "150 - 300" },
      { color: COLOR_RED, width: "30%", label: "Alerta", range: ">= 300" }
    ];

    if (numVal < 150) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 0, 150, 5, 40);
    } else if (numVal < 300) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 150, 300, 40, 70);
    } else {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 300, 800, 70, 95);
    }
  }
  else if (label === "Edad cardiovascular") {
    // Relativo a userAge. diff = value - userAge
    // Bueno: diff <= 0, Regular: diff 1-5, Alerta: diff >= 6
    segments = [
      { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "<= edad real" },
      { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "+1 a +5 años" },
      { color: COLOR_RED, width: "30%", label: "Alerta", range: ">= +6 años" }
    ];

    const diff = numVal - userAge;
    if (diff <= 0) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(diff, -10, 0, 5, 40);
    } else if (diff <= 5) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(diff, 0, 5, 40, 70);
    } else {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(diff, 5, 15, 70, 95);
    }
  }
  else if (label === "Riesgo CVD" || label === "Riesgo cardiovascular") {
    // Bueno: <5%, Regular: 5-10%, Alerta: >10%
    segments = [
      { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "< 5%" },
      { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "5% - 10%" },
      { color: COLOR_RED, width: "30%", label: "Alerta", range: "> 10%" }
    ];

    if (numVal < 5) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 0, 5, 5, 40);
    } else if (numVal <= 10) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 5, 10, 40, 70);
    } else {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 10, 20, 70, 95);
    }
  }
  else if (label === "Bienestar") {
    // Normalizado a 100. (Bienestar en Step3Clinical viene como /10, por ende numVal es <= 10)
    const val100 = numVal <= 10 ? numVal * 10 : numVal;
    segments = [
      { color: COLOR_RED, width: "35%", label: "Alerta", range: "< 50%" },
      { color: COLOR_YELLOW, width: "35%", label: "Regular", range: "50% - 79%" },
      { color: COLOR_GREEN, width: "30%", label: "Bueno", range: ">= 80%" }
    ];

    if (val100 < 50) {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(val100, 0, 50, 5, 35);
    } else if (val100 < 80) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(val100, 50, 80, 35, 70);
    } else {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(val100, 80, 100, 70, 95);
    }
  } else {
    return null;
  }

  return {
    statusLabel,
    statusColor,
    markerPosition,
    segments
  };
}

